/**
 * Mobile-internal bulk position assignment modal for Free Agents page.
 * Per Tony's mobile architecture rule: mobile-internal port of desktop BulkPositionAssignmentModal.
 *
 * Divergence from desktop (9o.6.a per audit F.2 prune-and-port):
 *   - PlayerLink replaced with mobile player-modal opener pattern (onPlayerClick prop)
 *   - Vertical stacked rows (one per player) replacing desktop's 5-column grid
 *   - Larger touch targets per mobile theme conventions
 *   - Inline styles + dynastyTheme tokens (no Tailwind class strings)
 *
 * Behavior preserved verbatim:
 *   - Auto-assignment via analyzeBulkAssignmentConflicts on open
 *   - Per-player assignment editing with conflict re-validation
 *   - Submit produces assignmentData per desktop API contract
 */

import React, { useState, useEffect, useMemo } from 'react';
import { X, AlertTriangle, AlertCircle, RefreshCw, UserPlus } from 'lucide-react';
import { dynastyTheme, withAlpha } from '../../../services/colorService';
import MobilePlayerLink from './MobilePlayerLink';
import {
  analyzeBulkAssignmentConflicts,
  validateBulkAssignments,
  getRosterCapacitySummary,
  isPlayerEligibleForPosition,
} from '../../../utils/RosterCapacityUtils';

const C = dynastyTheme.tokens.colors;

const MobileBulkPositionAssignmentModal = ({
  selectedPlayers,
  league,
  capacityAnalysis,
  currentRoster,
  onAssignAll,
  onCancel,
  isVisible = false,
  onPlayerClick = null,
}) => {
  const [assignments, setAssignments] = useState(new Map());
  const [conflicts, setConflicts] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [autoAssignComplete, setAutoAssignComplete] = useState(false);

  // Auto-assignment on modal open
  useEffect(() => {
    if (isVisible && selectedPlayers.length > 0 && !autoAssignComplete) {
      const result = analyzeBulkAssignmentConflicts(selectedPlayers, capacityAnalysis, league);

      const assignmentMap = new Map();
      result.assignments.forEach(({ player, assignment }) => {
        assignmentMap.set(player.ids?.league_player, assignment);
      });
      setAssignments(assignmentMap);
      setConflicts(result.conflicts);
      setWarnings(result.warnings);
      setAutoAssignComplete(true);
    }
  }, [isVisible, selectedPlayers, capacityAnalysis, league, autoAssignComplete]);

  const getAvailablePositionsForPlayer = (player) => {
    const options = [];

    Object.entries(capacityAnalysis.positionBreakdown).forEach(([position, breakdown]) => {
      if (isPlayerEligibleForPosition(player, position)) {
        const assignedToPosition = Array.from(assignments.values()).filter(
          (a) => a.position === position && a.type === 'active',
        ).length;
        const reallyAvailable = breakdown.availableSlots - assignedToPosition;
        options.push({
          value: `${position}_${breakdown.usedSlots + assignedToPosition}`,
          label: position.toUpperCase(),
          type: 'active',
          available: reallyAvailable > 0,
          slotInfo: `${breakdown.usedSlots + assignedToPosition + 1}/${breakdown.maxSlots}`,
        });
      }
    });

    if (capacityAnalysis.benchCapacity.maxSlots > 0) {
      const assignedToBench = Array.from(assignments.values()).filter((a) => a.type === 'bench').length;
      const reallyAvailable = capacityAnalysis.benchCapacity.availableSlots - assignedToBench;
      options.push({
        value: 'bench',
        label: 'Bench',
        type: 'bench',
        available: reallyAvailable > 0,
        slotInfo: `${capacityAnalysis.benchCapacity.usedSlots + assignedToBench + 1}/${capacityAnalysis.benchCapacity.maxSlots}`,
      });
    }

    if (capacityAnalysis.minorsCapacity.maxSlots > 0) {
      const assignedToMinors = Array.from(assignments.values()).filter((a) => a.type === 'minors').length;
      const reallyAvailable = capacityAnalysis.minorsCapacity.availableSlots - assignedToMinors;
      options.push({
        value: 'minors',
        label: 'Minor League',
        type: 'minors',
        available: reallyAvailable > 0,
        slotInfo: `${capacityAnalysis.minorsCapacity.usedSlots + assignedToMinors + 1}/${capacityAnalysis.minorsCapacity.maxSlots}`,
        note: 'No contract until called up',
      });
    }

    return options.sort((a, b) => {
      if (a.available !== b.available) return a.available ? -1 : 1;
      const priority = { active: 1, bench: 2, minors: 3 };
      return priority[a.type] - priority[b.type];
    });
  };

  const handleAssignmentChange = (player, newAssignmentValue) => {
    const newAssignments = new Map(assignments);
    if (newAssignmentValue === '') {
      newAssignments.delete(player.ids?.league_player);
    } else {
      const [position, type] =
        newAssignmentValue === 'bench' || newAssignmentValue === 'minors'
          ? [newAssignmentValue, newAssignmentValue]
          : [newAssignmentValue.split('_')[0], 'active'];

      newAssignments.set(player.ids?.league_player, {
        position,
        slotId: newAssignmentValue,
        type,
        reason: 'Manual selection',
        priority: 1,
      });
    }

    setAssignments(newAssignments);

    const validation = validateBulkAssignments(
      Array.from(newAssignments.values()).map((assignment) => ({ assignment })),
      capacityAnalysis,
    );
    if (!validation.isValid) {
      setConflicts(validation.errors.map((err) => ({ message: err.message })));
    } else {
      setConflicts([]);
    }
  };

  const handleSubmitAssignments = async () => {
    setProcessing(true);
    setError('');
    try {
      const assignmentData = selectedPlayers.map((player) => {
        const playerId = player.ids?.league_player;
        const assignment = assignments.get(playerId);
        return {
          player,
          roster_status: assignment?.type || 'bench',
          roster_position: assignment?.type === 'active' ? assignment.slotId : null,
          salary: assignment?.type === 'minors' ? 0 : player.league_context?.financial?.contract_salary,
          contract_years: assignment?.type === 'minors' ? 0 : 2,
          start_contract: assignment?.type !== 'minors',
        };
      });
      await onAssignAll(assignmentData);
    } catch (err) {
      setError(err.message || 'Failed to assign players');
    } finally {
      setProcessing(false);
    }
  };

  const assignmentSummary = useMemo(() => {
    const summary = { assigned: 0, unassigned: 0, active: 0, bench: 0, minors: 0, totalSalary: 0 };
    selectedPlayers.forEach((player) => {
      const playerId = player.ids?.league_player;
      const assignment = assignments.get(playerId);
      if (assignment) {
        summary.assigned += 1;
        if (assignment.type === 'active') summary.active += 1;
        else if (assignment.type === 'bench') summary.bench += 1;
        else if (assignment.type === 'minors') summary.minors += 1;
        if (assignment.type !== 'minors') {
          // 9o.6.b: NO FALLBACK. Matches desktop verbatim. If contract_salary is null,
          // NaN propagation surfaces the data integrity issue loudly rather than
          // silently masking it with zero.
          summary.totalSalary += player.league_context?.financial?.contract_salary;
        }
      } else {
        summary.unassigned += 1;
      }
    });
    return summary;
  }, [selectedPlayers, assignments]);

  if (!isVisible) return null;

  const canSubmit = assignments.size === selectedPlayers.length && conflicts.length === 0;
  const rosterSummary = capacityAnalysis ? getRosterCapacitySummary(capacityAnalysis) : null;

  return (
    <div data-testid="mobile-bulk-modal" style={styles.overlay}>
      <div style={styles.backdrop} onClick={onCancel} />
      <div style={styles.panel} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserPlus size={18} style={{ color: dynastyTheme.tokens.colors.primary }} />
            <div>
              <h2 style={styles.headerTitle}>Bulk Position Assignment</h2>
              <p style={styles.headerSubtitle}>
                Assign {selectedPlayers.length} player{selectedPlayers.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button type="button" onClick={onCancel} aria-label="Close" style={styles.closeButton}>
            <X size={20} />
          </button>
        </div>

        {/* Summary chips */}
        <div style={styles.summaryRow}>
          <SummaryChip label="Assigned" value={`${assignmentSummary.assigned}/${selectedPlayers.length}`} color={dynastyTheme.tokens.colors.success} />
          <SummaryChip label="Active" value={assignmentSummary.active} color={dynastyTheme.tokens.colors.primary} />
          <SummaryChip label="Bench/Minors" value={assignmentSummary.bench + assignmentSummary.minors} color={dynastyTheme.tokens.colors.info} />
          <SummaryChip label="Total $" value={`$${assignmentSummary.totalSalary}`} color={dynastyTheme.tokens.colors.success} />
        </div>

        {/* Conflicts & warnings */}
        {conflicts.length > 0 && (
          <div style={{ ...styles.banner, ...styles.bannerError }}>
            <AlertTriangle size={14} style={{ color: dynastyTheme.tokens.colors.error, flexShrink: 0 }} />
            <div style={{ flex: 1, fontSize: 12 }}>
              <div style={{ fontWeight: 700, color: dynastyTheme.tokens.colors.error, marginBottom: 4 }}>Conflicts</div>
              {conflicts.map((c, idx) => (
                <div key={idx} style={{ color: dynastyTheme.tokens.colors.error }}>• {c.message}</div>
              ))}
            </div>
          </div>
        )}
        {warnings.length > 0 && (
          <div style={{ ...styles.banner, ...styles.bannerWarning }}>
            <AlertCircle size={14} style={{ color: dynastyTheme.tokens.colors.warning, flexShrink: 0 }} />
            <div style={{ flex: 1, fontSize: 12 }}>
              <div style={{ fontWeight: 700, color: dynastyTheme.tokens.colors.warning, marginBottom: 4 }}>Warnings</div>
              {warnings.map((w, idx) => (
                <div key={idx} style={{ color: dynastyTheme.tokens.colors.warning }}>• {w.message}</div>
              ))}
            </div>
          </div>
        )}

        {/* Player rows */}
        <div style={styles.body}>
          {selectedPlayers.map((player) => {
            const playerId = player.ids?.league_player;
            const assignment = assignments.get(playerId);
            const availableOptions = getAvailablePositionsForPlayer(player);

            return (
              <div key={playerId} data-testid={`bulk-row-${playerId}`} style={styles.playerCard}>
                {/* Player header */}
                <div style={styles.playerHeader}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <MobilePlayerLink
                      mlbId={player.ids?.mlb}
                      name={
                        player.info?.full_name ||
                        `${player.info?.first_name || ''} ${player.info?.last_name || ''}`.trim() ||
                        'Unknown'
                      }
                      onClick={onPlayerClick}
                      style={{ fontWeight: 600, fontSize: 14 }}
                    />
                    <div style={styles.playerMeta}>
                      {player.info?.position || 'UTIL'} · {player.info?.mlb_team || '-'}
                    </div>
                  </div>
                  <div style={styles.salaryLabel}>
                    {assignment?.type === 'minors' ? (
                      <span style={{ color: dynastyTheme.tokens.colors.neutral[400], fontStyle: 'italic' }}>$0</span>
                    ) : (
                      <span style={{ color: dynastyTheme.tokens.colors.success, fontWeight: 600 }}>
                        ${player.league_context?.financial?.contract_salary || 0}
                      </span>
                    )}
                  </div>
                </div>

                {/* Position dropdown — full width for tap-friendliness */}
                <select
                  data-testid={`bulk-select-${playerId}`}
                  value={assignment?.slotId || ''}
                  onChange={(e) => handleAssignmentChange(player, e.target.value)}
                  style={{
                    ...styles.positionSelect,
                    borderColor: !assignment
                      ? dynastyTheme.tokens.colors.error
                      : dynastyTheme.tokens.colors.success,
                  }}
                >
                  <option value="">-- Select Position --</option>
                  {availableOptions.map((option) => (
                    <option key={option.value} value={option.value} disabled={!option.available}>
                      {option.label} ({option.slotInfo}){!option.available && ' - FULL'}
                      {option.note && ` - ${option.note}`}
                    </option>
                  ))}
                </select>

                {/* Status pill */}
                {assignment ? (
                  <div style={styles.statusPill}>
                    {assignment.position.toUpperCase()} · {assignment.type}
                  </div>
                ) : (
                  <div style={{ ...styles.statusPill, color: dynastyTheme.tokens.colors.error }}>
                    Unassigned
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Roster capacity strip */}
        {rosterSummary && (
          <div style={styles.capacityStrip}>
            <CapacityCell label="Active" used={rosterSummary.active.used + assignmentSummary.active} total={rosterSummary.active.total} />
            <CapacityCell label="Bench" used={rosterSummary.bench.used + assignmentSummary.bench} total={rosterSummary.bench.total} />
            <CapacityCell label="Minors" used={rosterSummary.minors.used + assignmentSummary.minors} total={rosterSummary.minors.total} />
            <CapacityCell label="Total" used={rosterSummary.overall.used + assignmentSummary.assigned} total={rosterSummary.overall.total} />
          </div>
        )}

        {error && (
          <div style={{ ...styles.banner, ...styles.bannerError, margin: '0 12px 12px' }}>
            <AlertTriangle size={14} style={{ color: dynastyTheme.tokens.colors.error }} />
            <span style={{ color: dynastyTheme.tokens.colors.error, fontSize: 12 }}>{error}</span>
          </div>
        )}

        {/* Footer */}
        <div style={styles.footer}>
          <button type="button" onClick={onCancel} disabled={processing} style={styles.cancelButton}>
            Cancel
          </button>
          {assignmentSummary.unassigned > 0 && (
            <button
              type="button"
              onClick={() => {
                setAutoAssignComplete(false);
                setAssignments(new Map());
              }}
              disabled={processing}
              style={styles.ghostButton}
              aria-label="Re-run auto-assign"
            >
              <RefreshCw size={14} />
              <span style={{ marginLeft: 4 }}>Re-run</span>
            </button>
          )}
          <button
            type="button"
            onClick={handleSubmitAssignments}
            disabled={!canSubmit || processing}
            style={{
              ...styles.submitButton,
              ...(!canSubmit || processing ? styles.submitButtonDisabled : {}),
            }}
          >
            {processing ? (
              <>
                <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} />
                <span style={{ marginLeft: 6 }}>Adding…</span>
              </>
            ) : (
              <>
                <UserPlus size={14} />
                <span style={{ marginLeft: 6 }}>
                  Add {assignmentSummary.assigned}
                  {assignmentSummary.totalSalary > 0 && ` ($${assignmentSummary.totalSalary})`}
                </span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};


const SummaryChip = ({ label, value, color }) => (
  <div style={{ ...summaryStyles.chip, color }}>
    <div style={summaryStyles.chipLabel}>{label}</div>
    <div style={{ ...summaryStyles.chipValue, color }}>{value}</div>
  </div>
);

const CapacityCell = ({ label, used, total }) => (
  <div style={{ flex: 1, textAlign: 'center' }}>
    <div style={{ fontSize: 9, color: dynastyTheme.tokens.colors.neutral[500], textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
    <div style={{ fontSize: 13, fontWeight: 700, color: C.white }}>{used}/{total}</div>
  </div>
);


const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 50,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
  },
  backdrop: {
    position: 'absolute',
    inset: 0,
    backgroundColor: withAlpha(C.black, 0.65),
    backdropFilter: 'blur(2px)',
  },
  panel: {
    position: 'relative',
    width: '100%',
    maxWidth: 480,
    maxHeight: '95vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: dynastyTheme.tokens.colors.neutral[900],
    border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
    borderRadius: 12,
    boxShadow: `0 25px 50px -12px ${withAlpha(C.black, 0.6)}`,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 14px',
    borderBottom: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
  },
  headerTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: C.white,
    margin: 0,
  },
  headerSubtitle: {
    fontSize: 11,
    color: dynastyTheme.tokens.colors.neutral[400],
    margin: 0,
  },
  closeButton: {
    background: 'transparent',
    border: 'none',
    color: dynastyTheme.tokens.colors.neutral[300],
    cursor: 'pointer',
    padding: 6,
    minWidth: 36,
    minHeight: 36,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  summaryRow: {
    display: 'flex',
    gap: 6,
    padding: '10px 12px',
    borderBottom: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
  },
  banner: {
    margin: '8px 12px 0',
    padding: 8,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
  },
  bannerError: {
    backgroundColor: withAlpha(C.error, 0.1),
    border: `1px solid ${dynastyTheme.tokens.colors.error}33`,
  },
  bannerWarning: {
    backgroundColor: withAlpha(C.goldBase, 0.1),
    border: `1px solid ${dynastyTheme.tokens.colors.warning}33`,
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  playerCard: {
    backgroundColor: dynastyTheme.tokens.colors.neutral[800],
    border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
    borderRadius: 8,
    padding: 10,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  playerHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
  },
  playerMeta: {
    fontSize: 11,
    color: dynastyTheme.tokens.colors.neutral[400],
    marginTop: 2,
  },
  salaryLabel: {
    fontSize: 13,
  },
  positionSelect: {
    width: '100%',
    minHeight: 40,
    padding: '8px 10px',
    fontSize: 13,
    color: C.white,
    backgroundColor: dynastyTheme.tokens.colors.neutral[900],
    border: '1px solid',
    borderRadius: 6,
    outline: 'none',
    cursor: 'pointer',
  },
  statusPill: {
    alignSelf: 'flex-start',
    padding: '4px 8px',
    fontSize: 11,
    fontWeight: 600,
    borderRadius: 4,
    backgroundColor: withAlpha(C.success, 0.15),
    color: dynastyTheme.tokens.colors.success,
  },
  capacityStrip: {
    display: 'flex',
    gap: 8,
    padding: '10px 12px',
    borderTop: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
  },
  footer: {
    display: 'flex',
    gap: 8,
    padding: '12px 14px',
    borderTop: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
  },
  cancelButton: {
    padding: '10px 14px',
    minHeight: 44,
    borderRadius: 6,
    backgroundColor: dynastyTheme.tokens.colors.neutral[800],
    color: dynastyTheme.tokens.colors.neutral[200],
    border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  ghostButton: {
    padding: '10px 12px',
    minHeight: 44,
    borderRadius: 6,
    backgroundColor: 'transparent',
    color: dynastyTheme.tokens.colors.neutral[300],
    border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
    fontSize: 12,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
  },
  submitButton: {
    flex: 1,
    padding: '10px 16px',
    minHeight: 44,
    borderRadius: 6,
    backgroundColor: dynastyTheme.tokens.colors.primary,
    color: C.black,
    border: 'none',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  submitButtonDisabled: {
    backgroundColor: dynastyTheme.tokens.colors.neutral[700],
    color: dynastyTheme.tokens.colors.neutral[500],
    cursor: 'not-allowed',
  },
};

const summaryStyles = {
  chip: {
    flex: 1,
    minWidth: 0,
    padding: '6px 8px',
    backgroundColor: dynastyTheme.tokens.colors.neutral[800],
    border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
    borderRadius: 6,
    textAlign: 'center',
  },
  chipLabel: {
    fontSize: 9,
    color: dynastyTheme.tokens.colors.neutral[400],
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  chipValue: {
    fontSize: 14,
    fontWeight: 700,
    marginTop: 2,
  },
};

export default MobileBulkPositionAssignmentModal;
